import { WarningIcon } from "@chakra-ui/icons";
import { Box, Flex, HStack, Stack } from "@chakra-ui/layout";
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay } from "@chakra-ui/modal";
import { Button, Image, Radio, RadioGroup, Text, Tooltip } from "@chakra-ui/react";
import { useContractFunction, useEthers } from "@usedapp/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NftResource } from "../../../components/NftResource";
import { ProgressIcon } from "../../../components/ProgressIcon";
import { colors } from "../../../core/constants/colors";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { APPROVE_STRATEGY, MINT_TYPE, NFT_TYPE } from "../../../core/constants/nft";
import { useNftProxyAddress } from "../../../core/hooks/useAddress";
import { useNftApproval } from "../../../core/hooks/useApproval";
import { useERC721Contract } from "../../../core/hooks/useContract";
import { IMoralisNft } from "../../../core/models/nft";
import { IToken } from "../../../core/models/token";
import { formatNumber } from "../../../core/utils/number";
import { ethers } from "ethers";

interface Props {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isConfirming: boolean;
  nft: IMoralisNft;
  price: number;
  token: IToken;
  confirmError?: string;
  quantity: number;
}

enum STEP {
  APPROVE_TOKEN,
  LIST,
}

export const CompleteListingModal = React.memo((props: Props) => {
  const { account } = useEthers();
  const [approveStrategy, setApproveStrategy] = useState(APPROVE_STRATEGY.ONLY_ITEM);
  const collectionContract = useERC721Contract(props?.nft.token_address);
  const nftTransferProxyAddress = useNftProxyAddress();
  const { t } = useTranslation([I18N_NAMESPACE.ASSET_DETAIL]);

  const [step, setStep] = useState(STEP.APPROVE_TOKEN);

  const [isApproving, setIsApproving] = useState(false);
  const [approveError, setApproveError] = useState<string>();

  const getIsApproved = async () => {
    const isApprovedItem =
      props.nft.contract_type === NFT_TYPE.ERC_721 && props.nft.mint_type === MINT_TYPE.NORMAL
        ? await collectionContract?.getApproved(props.nft.token_id)
        : ethers.constants.AddressZero;

    const isApprovedForAll = await collectionContract?.isApprovedForAll(account, nftTransferProxyAddress);

    return isApprovedForAll || isApprovedItem === nftTransferProxyAddress;
  };

  const onApprove = async () => {
    try {
      setIsApproving(true);
      setApproveError(undefined);

      await approve();

      setStep(STEP.LIST);
    } catch (e: any) {
      setApproveError(e?.data?.message || e?.message);
    } finally {
      setIsApproving(false);
    }
  };

  const approve = useCallback(async () => {
    const tx = await (approveStrategy === APPROVE_STRATEGY.ALL ||
    props.nft.contract_type === NFT_TYPE.ERC_1155 ||
    props.nft.mint_type === MINT_TYPE.LAZY
      ? collectionContract?.setApprovalForAll(nftTransferProxyAddress, true)
      : collectionContract?.approve(nftTransferProxyAddress, props.nft.token_id));

    await tx.wait();
  }, []);

  const onApproveTokenStep = async () => {
    try {
      setIsApproving(true);
      setApproveError(undefined);

      const isApproved = await getIsApproved();

      if (isApproved) {
        setStep(STEP.LIST);
        return;
      }

      if (!isApproved && (props.nft.contract_type === NFT_TYPE.ERC_1155 || props.nft.mint_type === MINT_TYPE.LAZY)) {
        await approve();
      }
    } catch (e: any) {
      setApproveError(e?.data?.message || e?.message);
    } finally {
      setIsApproving(false);
    }
  };

  useEffect(() => {
    if (!props.isVisible) return;

    const executionByStep = {
      [STEP.APPROVE_TOKEN]: onApproveTokenStep,
      [STEP.LIST]: props.onConfirm,
    };

    const execution = executionByStep[step];
    if (execution) {
      execution();
    }
  }, [props.isVisible, step]);

  return (
    <>
      <Modal isOpen={props.isVisible} onClose={props.onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Complete Listing")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody marginTop="12px">
            <Flex justifyContent="center" alignItems="center">
              <Box borderWidth="1px" borderRadius="lg" height="80px" width="80px">
                <NftResource image={props.nft.metadata?.image} animationUrl={props.nft.metadata?.animation_url} muted />
              </Box>

              <Flex flex="1" marginLeft="12px" justifyContent="space-between">
                <Box>
                  <Box fontSize={"small"}>{props.nft?.name}</Box>
                  <Text color={colors.primary2} fontWeight="700" fontSize="20px">
                    {props.nft?.metadata?.name}
                  </Text>
                </Box>

                <Flex flexDirection="column" alignItems="flex-end">
                  <Text color={colors.primary2} fontWeight="500">
                    {t("Price")}
                  </Text>
                  <Flex marginTop="4px">
                    <Image src={props.token?.logoUrl} height="20px" width="20px" marginRight="4px" />
                    <Text fontWeight="700" color={colors.primary}>
                      {formatNumber(props.price * props.quantity)}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>

            <HStack alignItems="flex-start" borderRadius="lg" marginTop="20px" background="#F6F6F6" padding="20px 16px">
              <ProgressIcon isLoading={isApproving} isSuccess={step > STEP.APPROVE_TOKEN} />
              <Box flex={1}>
                <Text fontWeight="700" color={colors.primary2}>
                  {t("Approve")}
                </Text>
                <Text fontSize="14px" color="#000" marginTop="8px">
                  {t("To get set up for listing for the first time, you must approve this item for sale.")}
                </Text>

                {!isApproving &&
                  props.nft.contract_type !== NFT_TYPE.ERC_1155 &&
                  props.nft.mint_type !== MINT_TYPE.LAZY &&
                  step === STEP.APPROVE_TOKEN && (
                    <>
                      <RadioGroup
                        onChange={setApproveStrategy}
                        value={approveStrategy}
                        marginTop={"16px"}
                        marginLeft="16px"
                        colorScheme="blue"
                      >
                        <Stack>
                          <Radio value={APPROVE_STRATEGY.ONLY_ITEM}>
                            <Text fontSize="14px" color={colors.primary2} fontWeight="500">
                              {t("Approve this NFT only.")}
                            </Text>
                          </Radio>
                          <Radio value={APPROVE_STRATEGY.ALL}>
                            <Flex>
                              <Text fontSize="14px" color={colors.primary2} fontWeight="500">
                                {t("Approve all of my NFTs in this collection.")}
                              </Text>
                              <Tooltip
                                hasArrow
                                label={t(
                                  "Choosing this option so you won't need to approve any NFT for sale from this collection (save gas fee)."
                                )}
                                borderRadius="lg"
                                padding="6px"
                                placement="top"
                              >
                                <WarningIcon w={4} h={4} color={colors.primary} marginLeft="6px" />
                              </Tooltip>
                            </Flex>
                          </Radio>
                        </Stack>
                      </RadioGroup>
                      {!approveError && (
                        <Box marginTop="8px">
                          <Button colorScheme="blue" onClick={onApprove} isLoading={isApproving} marginTop={2}>
                            {t("Approve")}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}

                {!!approveError && (
                  <Box marginTop="8px">
                    <Text color={"red.500"} fontSize={"md"}>
                      {approveError}
                    </Text>
                    <Button colorScheme="blue" onClick={onApprove} isLoading={isApproving} marginTop={2}>
                      {t("Try again")}
                    </Button>
                  </Box>
                )}
              </Box>
            </HStack>

            <HStack alignItems="flex-start" borderRadius="lg" marginTop="20px" background="#F6F6F6" padding="20px 16px">
              <ProgressIcon isLoading={step === STEP.LIST && props.isConfirming} isSuccess={false} />
              <Box flex={1}>
                <Text fontWeight="700" color={colors.primary2}>
                  {t("Put on sale")}
                </Text>
                <Text fontSize="14px" color="#000" marginTop="8px">
                  {t("Sign your wallet to confirm item price")}
                </Text>
                <Box marginTop="20px">
                  {!!props.confirmError && (
                    <Box>
                      <Text color={"red.500"} fontSize={"md"}>
                        {props.confirmError}
                      </Text>
                      <Button colorScheme="blue" isLoading={props.isConfirming} onClick={props.onConfirm} marginTop={2}>
                        {t("Try again")}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
});
